import axiosInstance from './axiosInstance';

export interface Task {
  _id: string;
  taskId: string; // Unique, editable, starts with 'PP-'
  taskTitle: string;
  priority: string;
  description: string;
  type: string;
  project?:
    | {
        _id: string;
        projectNumber: string;
        projectName: string;
      }
    | string; // Can be populated object or just ID string
  authBy?: string[]; // Array of user IDs who authorized the task
  dateRequested?: Date | string;
  quotedPrice?: number;
  agreedPrice?: number;
  dateStarted?: Date | string;
  dueDate?: Date | string;
  status: string;
  comments: string[];
  assignedToUser?: string[]; // Array of user IDs
  createdBy?: string | { _id: string; firstName: string; lastName: string; email: string }; // User who created the task (owner)
  createdAt: Date | string;
  updatedAt: Date | string;
  // Status history
  statusHistory?: {
    _id?: string;
    status: string;
    changedBy: string | { _id: string; firstName: string; lastName: string; email: string };
    changedAt: Date | string;
    previousStatus?: string;
  }[];
  // Subtask fields
  parentTask?: string; // ID of parent task
  subtasks?: string[] | Task[]; // Array of subtask IDs or populated subtasks
  isSubtask?: boolean;
  subtaskOrder?: number;
}

export interface CreateTaskData {
  taskId?: string; // Optional on create, will be auto-generated if not provided
  taskTitle: string;
  priority: string;
  description: string;
  type: string;
  project?: string; // Project ID
  authBy?: string[]; // Array of user IDs who authorized the task
  dateRequested?: Date | string;
  quotedPrice?: number;
  agreedPrice?: number;
  dateStarted?: Date | string;
  dueDate?: Date | string;
  status?: string;
  comments?: string[];
  assignedToUser?: string[]; // Array of user IDs
  // Subtask fields
  parentTask?: string;
  isSubtask?: boolean;
  subtaskOrder?: number;
}

export interface UpdateTaskData extends Partial<CreateTaskData> {
  taskId?: string; // Editable
  taskTitle?: string;
  done?: boolean;
}

// API functions
// Fetch tasks, optionally show all if allowed
export const fetchTasks = async (showAll: boolean = false): Promise<Task[]> => {
  const response = await axiosInstance.get(`/tasks${showAll ? '?all=true' : ''}`);
  return response.data;
};

export const fetchTaskById = async (id: string): Promise<Task> => {
  const response = await axiosInstance.get(`/tasks/${id}`);
  return response.data;
};

export const createTask = async (taskData: CreateTaskData): Promise<Task> => {
  // No need to pass token, axiosInstance handles it
  const response = await axiosInstance.post('/tasks', taskData);
  return response.data;
};

export const updateTask = async (id: string, taskData: UpdateTaskData): Promise<Task> => {
  const response = await axiosInstance.put(`/tasks/${id}`, taskData);
  return response.data;
};

export const deleteTask = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/tasks/${id}`);
};

// --- Task API with Auth ---
export const fetchTaskByIdAuth = async (id: string) => {
  return axiosInstance.get(`/tasks/${id}`);
};

export const fetchComments = async (id: string) => {
  return axiosInstance.get(`/tasks/${id}/comments`);
};

export const addComment = async (id: string, comment: string, files?: File[]) => {
  const formData = new FormData();
  formData.append('comment', comment);
  if (files && files.length > 0) {
    files.forEach(f => formData.append('files', f));
  }
  return axiosInstance.post(`/tasks/${id}/comments`, formData);
};

// Add comment with Cloudinary images
export const addCommentWithImages = async (id: string, comment: string, images?: string[]) => {
  return axiosInstance.post(`/tasks/${id}/comments`, {
    comment,
    images: images || []
  });
};

export const editComment = async (id: string, commentId: string, text: string) => {
  return axiosInstance.patch(
    `/tasks/${id}/comments/${commentId}`,
    { text }
  );
};

export const deleteComment = async (id: string, commentId: string) => {
  return axiosInstance.delete(`/tasks/${id}/comments/${commentId}`);
};

export const fetchFiles = async (id: string) => {
  return axiosInstance.get(`/tasks/${id}/files`);
};

export const uploadFile = async (id: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return axiosInstance.post(`/tasks/${id}/files`, formData);
};

export const deleteFile = async (id: string, fileId: string) => {
  return axiosInstance.delete(`/tasks/${id}/files/${fileId}`);
};

// Reply API functions
export const addReply = async (taskId: string, commentId: string, text: string, images?: string[], parentReplyId?: string) => {
  return axiosInstance.post(
    `/tasks/${taskId}/comments/${commentId}/replies`,
    { text, images: images || [], parentReplyId }
  );
};

export const deleteReply = async (taskId: string, commentId: string, replyId: string) => {
  return axiosInstance.delete(`/tasks/${taskId}/comments/${commentId}/replies/${replyId}`);
};

export const editReply = async (taskId: string, commentId: string, replyId: string, text: string) => {
  return axiosInstance.put(`/tasks/${taskId}/comments/${commentId}/replies/${replyId}`, { text });
};

// Like/dislike a comment
export const likeOrDislikeComment = async (
  taskId: string,
  commentId: string,
  action: 'like' | 'dislike'
) => {
  return axiosInstance.post(`/tasks/${taskId}/comments/${commentId}/like`, { action });
};

// Like/dislike a reply
export const likeOrDislikeReply = async (
  taskId: string,
  commentId: string,
  replyId: string,
  action: 'like' | 'dislike'
) => {
  return axiosInstance.post(`/tasks/${taskId}/comments/${commentId}/replies/${replyId}/like`, { action });
};

// ===============================
// SUBTASK API FUNCTIONS
// ===============================

export interface CreateSubtaskData {
  taskTitle: string;
  priority: string;
  description: string;
  status: string;
  authBy?: string[];
  dateRequested?: Date | string;
  dateStarted?: Date | string;
  dueDate?: Date | string;
  assignedToUser?: string[];
}

export interface TaskHierarchy {
  parentTask: Task;
  subtasks: Task[];
  totalSubtasks: number;
  completedSubtasks: number;
}

// Create a subtask for a parent task
export const createSubtask = async (parentTaskId: string, data: CreateSubtaskData) => {
  return axiosInstance.post(`/tasks/${parentTaskId}/subtasks`, data);
};

export const createSubtasksBulk = async (
  parentTaskId: string,
  subtasks: CreateSubtaskData[]
) => {
  return axiosInstance.post(`/tasks/${parentTaskId}/subtasks/bulk`, { subtasks });
};

// Get all subtasks for a parent task
export const getSubtasks = async (parentTaskId: string) => {
  return axiosInstance.get(`/tasks/${parentTaskId}/subtasks`);
};

// Get a task with all its subtasks populated
export const getTaskWithSubtasks = async (taskId: string) => {
  return axiosInstance.get(`/tasks/${taskId}/with-subtasks`);
};

// Update subtask order
export const updateSubtaskOrder = async (subtaskId: string, newOrder: number) => {
  return axiosInstance.patch(`/tasks/subtasks/${subtaskId}/order`, { newOrder });
};

// Delete a subtask
export const deleteSubtask = async (subtaskId: string) => {
  return axiosInstance.delete(`/tasks/subtasks/${subtaskId}`);
};

// Get task hierarchy (parent with all subtasks and metadata)
export const getTaskHierarchy = async (taskId: string) => {
  return axiosInstance.get(`/tasks/${taskId}/hierarchy`);
};
